import type { WorkerLike } from './extractPdf';
import {
  MaterialPrepError,
  OpenPdfRenderer,
  PdfRenderer,
  RenderMessage,
  RenderRequest,
} from './extractionTypes';

const abortError = (): DOMException => new DOMException('Aborted', 'AbortError');

/**
 * Opens `file` in a freshly created render worker and resolves, once the worker has loaded it,
 * a `PdfRenderer` that draws one page per `render` call (PHO-D9). Several renders may be pending
 * at once; each resolves on its own page's image. A failure after opening breaks the renderer:
 * every pending and later render rejects with it. `close()` and an abort of `signal` reject what
 * is pending with an `AbortError`. The worker is terminated exactly once, however this ends, and
 * every listener is removed (PTX-D9).
 */
export const openPdfRendererWith = (
  createWorker: () => WorkerLike,
  file: Blob,
  signal: AbortSignal
): Promise<PdfRenderer> =>
  new Promise<PdfRenderer>((resolveOpen, rejectOpen) => {
    if (signal.aborted) {
      rejectOpen(abortError());
      return;
    }

    let worker: WorkerLike | undefined;
    let opened = false;
    /** Set once the renderer has ended; every later `render` rejects with it. */
    let endError: Error | undefined;
    const pending = new Map<
      number,
      { resolve: (image: Blob) => void; reject: (e: Error) => void }
    >();

    const post = (request: RenderRequest, transfer: Transferable[] = []): void => {
      worker?.postMessage(request, transfer);
    };

    /** Ends the renderer with `error`: rejects the open or every pending render, then tears down. */
    const end = (error: Error): void => {
      if (endError) {
        return;
      }
      endError = error;
      if (worker) {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
        worker.removeEventListener('messageerror', onError);
        worker.terminate();
      }
      signal.removeEventListener('abort', onAbort);
      if (!opened) {
        rejectOpen(error);
      }
      for (const { reject } of pending.values()) {
        reject(error);
      }
      pending.clear();
    };

    const renderer: PdfRenderer = {
      render: (page) => {
        if (endError) {
          return Promise.reject(endError);
        }
        if (pending.has(page)) {
          return Promise.reject(new Error(`Page ${page} is already being rendered.`));
        }
        return new Promise<Blob>((resolve, reject) => {
          pending.set(page, { resolve, reject });
          post({ type: 'render', page });
        });
      },
      close: () => end(abortError()),
    };

    const onMessage = (event: MessageEvent<RenderMessage>): void => {
      const message = event.data;
      if (message?.type === 'opened') {
        if (!opened) {
          opened = true;
          resolveOpen(renderer);
        }
      } else if (message?.type === 'image') {
        const request = pending.get(message.page);
        if (request) {
          pending.delete(message.page);
          request.resolve(message.image);
        }
      } else if (message?.type === 'failed') {
        // A `failed` carries no page number, so it cannot be matched to one render; it fails
        // every pending one (PHO-D9).
        end(new MaterialPrepError(message.code));
      }
      // Any other message (e.g. pdf.js's own worker module posting to the main thread) is ignored.
    };

    const onError = (): void => {
      end(new MaterialPrepError('unknown'));
    };

    const onAbort = (): void => {
      end(abortError());
    };

    signal.addEventListener('abort', onAbort);

    file
      .arrayBuffer()
      .then((data) => {
        if (endError) {
          return;
        }
        worker = createWorker();
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        worker.addEventListener('messageerror', onError);
        post({ type: 'open', data }, [data]);
      })
      .catch(() => {
        end(new MaterialPrepError('unknown'));
      });
  });

/**
 * Opens `file` for rendering in a dedicated module Web Worker (PHO-D9). The
 * `new Worker(new URL(...), { type: 'module' })` shape must be written out literally here: Vite
 * only recognises that exact pattern when bundling the worker.
 */
export const openPdfRenderer: OpenPdfRenderer = (file, signal) =>
  openPdfRendererWith(
    () => new Worker(new URL('./render.worker.ts', import.meta.url), { type: 'module' }),
    file,
    signal
  );
